import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withTranslation } from 'react-i18next'

import placeholderLogo from 'images/icons/placeholder.png'

import { browserHelpers } from 'utils'
import stringHelpers from 'utils/stringHelpers'
import { getStringFromSeconds, formatMilliSeconds } from 'utils/dateHelpers'
import { NotificationType } from 'constants/enums'
import { PLAY_TRIGGERS } from 'constants/tracking'
import { ReleasesService } from 'services'

import ModalActions from 'reducers/Modal'
import NotificationActions from 'reducers/Notification'
import { thunks as DownloadsThunks } from 'reducers/Downloads'
import ReleaseActions, { thunks as ReleaseThunks } from 'reducers/Releases'
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList'
import { thunks as PlayQueueThunks } from 'reducers/PlayQueue'

import { Table2 } from 'components'

import ReleaseOptions from 'components/ReleaseOptions'

import formatters from './Formatters'

const mapStateToProps = ({ global, options, playList, profile }) => ({
  markets: options.markets,
  playlists: playList.playlistAbstracts,
  isIpadView: global.isIpadView,
  format: profile.profile.format
})

@connect(mapStateToProps)
class ReleasesTable extends React.PureComponent {
  static propTypes = {
    flagged: PropTypes.bool,
    showMarkets: PropTypes.bool,
    showPlayQueueOptions: PropTypes.bool,
    modalProps: PropTypes.object,
    modalComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    items: PropTypes.arrayOf(PropTypes.object),
    markets: PropTypes.arrayOf(PropTypes.object),
    playlists: PropTypes.arrayOf(PropTypes.object),
    downloadSource: PropTypes.string,
    playSource: PropTypes.string,

    t: PropTypes.func.isRequired,
    renderModal: PropTypes.func,
    onPlay: PropTypes.func,
    dispatch: PropTypes.func.isRequired,
    isIpadView: PropTypes.bool,
    format: PropTypes.string
  }
  static defaultProps = {
    modalProps: null,
    downloadSource: 'releases_table'
  }

  state = {
    showModal: false,
    modalRelease: null
  }

  handleShowModal = (release) => {
    const { dispatch } = this.props
    dispatch(
      ReleaseThunks.getDetail(release.releaseid, (releaseDetail) => {
        dispatch(ReleaseActions.toggleReleaseInModal(releaseDetail))
      })
    )
  }

  handleToggleModal = ({ releaseid }) =>
    this.setState((state) => {
      const { showModal, modalRelease } = state

      const newState =
        releaseid === modalRelease
          ? { showModal: !showModal }
          : { showModal: true, modalRelease: releaseid }

      return newState
    })

  handleCloseModal = ({ releaseid }) => {
    const { modalRelease } = this.state

    if (releaseid === modalRelease) {
      this.setState({ showModal: false, modalRelease: null })
    }
  }

  handlePlayRelease = (release) => {
    const { dispatch, playSource } = this.props
    dispatch(
      PlayQueueThunks.playRelease(release.releaseid, {
        source: playSource,
        trigger: PLAY_TRIGGERS.ROW
      })
    )
  }

  handlePlayNext = (release) => {
    const { dispatch } = this.props
    dispatch(PlayQueueThunks.insertReleaseNext(release.releaseid))
  }

  handlePlayLater = (release) => {
    const { dispatch } = this.props
    dispatch(PlayQueueThunks.insertReleaseAtBottom(release.releaseid))
  }

  handleDownloadRelease = (release) => {
    const { dispatch, format, downloadSource } = this.props
    dispatch(
      DownloadsThunks.addReleaseToDownloadQueue(
        release,
        format,
        downloadSource
      )
    )
  }

  handleAddToPlaylist = (playlist, release) => {
    const { dispatch } = this.props

    const msg = `${release.artist} - ${release.title}`

    let releaseTrackIds = []

    ReleasesService.getDetail(release.releaseid).then((releaseDetail) => {
      releaseTrackIds = releaseDetail.data.songs
        .filter((s) => s.assettype == 'audio')
        .map((item) => item.trackid)
      dispatch(
        PlaylistThunks.addTracks(playlist.playlistid, releaseTrackIds, () =>
          dispatch(
            NotificationActions.broadcastMessage(
              {
                songTitle: msg,
                playlistTitle: playlist.name
              },
              NotificationType.addedToPlaylist
            )
          )
        )
      )
    })
  }

  handleCreatePlaylist = (release) => {
    const { dispatch } = this.props
    let releaseTrackIds = []

    ReleasesService.getDetail(release.releaseid).then((releaseDetail) => {
      releaseTrackIds = releaseDetail.data.songs
        .filter((s) => s.assettype == 'audio')
        .map((item) => item.trackid)
      dispatch(PlaylistActions.setSongsToAdd(releaseTrackIds))
      dispatch(ModalActions.togglePlaylistModal())
    })
  }

  insertIds = () => {
    const { items } = this.props
    this._itemsWithIds = items.map((item) => ({
      ...item,
      id: item.releaseid
    }))
  }

  renderModal = (release, hovered) => {
    const { showModal, modalRelease } = this.state
    const { playlists, showPlayQueueOptions, modalComponent, modalProps } =
      this.props
    const { candownload } = release

    const isDownloadAllowed =
      candownload && browserHelpers.browserAllowsDownload

    const showCurrentModal = showModal && modalRelease === release.releaseid

    const hideTrigger = !(hovered || showCurrentModal)

    const Component = !modalComponent ? ReleaseOptions : modalComponent

    return (
      <Component
        release={release}
        playlists={playlists}
        isOpen={showCurrentModal}
        hideTrigger={hideTrigger}
        showPlayQueueOptions={showPlayQueueOptions}
        isDownloadAllowed={isDownloadAllowed}
        onDownloadClick={this.handleDownloadRelease}
        onPlayNextClick={this.handlePlayNext}
        onPlayLaterClick={this.handlePlayLater}
        onAddTrackToPlaylist={this.handleAddToPlaylist}
        onCreatePlaylist={this.handleCreatePlaylist}
        onToggle={() => this.handleToggleModal(release)}
        {...modalProps}
      />
    )
  }

  render() {
    this.insertIds()
    const {
      t,
      renderModal,
      onPlay,
      flagged,
      markets,
      showMarkets,
      isIpadView
    } = this.props
    const items = this._itemsWithIds
    const { limitLength } = stringHelpers

    // use no flex shrink so items don't shrink to fit in flex box, and instead will horizontal scroll
    // if you need to fill space available use flex-grow instead
    const flexClasses = 'flex-shrink-0'

    const columns = [
      {
        image: true,
        src: 'coverart',
        cellStyleName: 'release-coverart',
        fallback: placeholderLogo
      },
      {
        title: '',
        // width: 100,
        field: 'market_tags',
        cellClassName: 'flex-none',
        cellStyleName: 'release-market',
        format: (field) => formatters.formatMarketsCell(markets, field)
      },
      {
        title: t('release-list.artist'),
        // width: isIpadView ? 190 : 150,
        sort: 'artist',
        field: 'artist',
        resizable: true,
        cellClassName: `truncate ${flexClasses}`,
        cellStyleName: 'release-artist'
      },
      {
        title: t('release-list.title'),
        // width: isIpadView ? 240 : 200,
        sort: 'title',
        field: 'title',
        resizable: true,
        cellClassName: `truncate ${flexClasses}`,
        cellStyleName: 'release-title'
      },
      {
        title: t('release-list.duration'),
        // width: 120,
        sort: 'duration',
        field: 'duration',
        cellClassName: `${flexClasses}`,
        cellStyleName: 'release-duration',
        resizable: true,
        format: (field) => getStringFromSeconds(field)
      },
      {
        title: t('release-list.tracks'),
        // width: 60,
        field: flagged ? 'flaggedtrackcount' : 'trackcount',
        resizable: true,
        cellClassName: `${flexClasses}`,
        cellStyleName: 'release-tracks'
      },
      {
        title: t('release-list.available-date'),
        // width: 170,
        sort: 'available',
        field: 'available',
        cellClassName: `truncate ${flexClasses}`,
        resizable: true,
        format: (field) => formatMilliSeconds(field),
        cellStyleName: 'release-date'
      },
      {
        title: t('release-list.label'),
        // width: 150,
        field: 'labels',
        cellClassName: `break-words ${flexClasses}`,
        cellStyleName: 'release-label',
        resizable: true,
        format: (field) => formatters.formatLabelCell(field)
      },
      {
        title: t('release-list.formats'),
        // width: 150,
        field: flagged ? 'genre' : 'genres',
        cellClassName: `break-words ${flexClasses}`,
        cellStyleName: 'release-format',
        resizable: true,
        format: (field) => limitLength(field.join(', '), 30)
      }
      // {
      // 	title: t('release-list.markets'),
      // 	// width: 100,
      // 	field: 'market_tags',
      //   cellClassName: 'flex-none',
      //   cellStyleName: 'release-market',
      // 	format: field => formatters.formatMarketsCell(markets, field)
      // }
    ]

    return (
      <Table2
        controlledHover
        tableId="releases"
        items={items}
        columns={columns}
        renderModal={renderModal || this.renderModal}
        onClick={this.handleShowModal}
        onPlay={onPlay || this.handlePlayRelease}
        onHoverStop={this.handleCloseModal}
      />
    )
  }
}

export default withTranslation()(ReleasesTable)
